/**
 * Sets the value at `path` of `object`. If a portion of `path` doesn't exist,
 * it's created. Arrays are created for missing index properties while objects
 * are created for all other missing properties.
 *
 * **Note:** This method mutates `object`.
 *
 * @param {Object} object The object to modify.
 * @param {Array|string} path The path of the property to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns `object`.
 * @example
 *
 * const object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * SetTask(object, 'a[0].b.c', 4);
 * console.log(object.a[0].b.c);
 * // => 4
 *
 * SetTask(object, ['x', '0', 'y', 'z'], 5);
 * console.log(object.x[0].y.z);
 * // => 5
 */
export function SetTask(object: any, path: string | string[], value: any): any {
    return object == null ? object : baseSet(object, path, value)
}

/**
 * The base implementation of `set`.
 * @private
 */
function baseSet(object: any, path: string | string[], value: any, customizer?: Function): any {
    if (!isObject(object)) {
        return object
    }
    const pathArray = castPath(path, object)

    let index = -1
    const length = pathArray.length
    const lastIndex = length - 1
    let nested = object

    while (nested != null && ++index < length) {
        const key = toKey(pathArray[index])
        let newValue = value

        if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
            return object
        }

        if (index !== lastIndex) {
            const objValue = nested[key]
            newValue = customizer ? customizer(objValue, key, nested) : undefined
            if (newValue === undefined) {
                newValue = isObject(objValue) ? objValue : isIndex(pathArray[index + 1]) ? [] : {}
            }
        }
        assignValue(nested, key, newValue)
        nested = nested[key]
    }
    return object
}

/**
 * Assigns `value` to `key` of `object` if the existing value is not equivalent
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 * @private
 */
function assignValue(object: any, key: string, value: any): void {
    const objValue = object[key]
    if (
        !(hasOwnProperty.call(object, key) && eq(objValue, value)) ||
        (value === undefined && !(key in object))
    ) {
        baseAssignValue(object, key, value)
    }
}

/**
 * The base implementation of `assignValue` and `assignMergeValue` without
 * value checks.
 * @private
 */
function baseAssignValue(object: any, key: string, value: any): void {
    if (key === '__proto__' && defineProperty) {
        defineProperty(object, key, {
            configurable: true,
            enumerable: true,
            value: value,
            writable: true,
        })
    } else {
        object[key] = value
    }
}

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 * @private
 */
function eq(value: any, other: any): boolean {
    return value === other || (value !== value && other !== other)
}

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 * @private
 */
function isObject(value: any): boolean {
    const type = typeof value
    return value != null && (type === 'object' || type === 'function')
}

/**
 * Checks if `value` is a valid array-like index.
 * @private
 */
function isIndex(value: any, length?: number): boolean {
    const type = typeof value
    length = length == null ? MAX_SAFE_INTEGER : length

    return (
        !!length &&
        (type === 'number' || (type !== 'symbol' && reIsUint.test(value))) &&
        value > -1 &&
        value % 1 === 0 &&
        value < length
    )
}

/**
 * Casts `value` to a path array if it's not one.
 * @private
 */
function castPath(value: string | string[], object?: any): string[] {
    if (Array.isArray(value)) {
        return value
    }
    return isKey(value, object) ? [value] : stringToPath(toString(value))
}

/**
 * Checks if `value` is a property name and not a property path.
 * @private
 */
function isKey(value: string, object?: any): boolean {
    if (Array.isArray(value)) {
        return false
    }
    const type = typeof value
    if (
        type === 'number' ||
        type === 'symbol' ||
        type === 'boolean' ||
        value == null ||
        isSymbol(value)
    ) {
        return true
    }
    return (
        reIsPlainProp.test(value) ||
        !reIsDeepProp.test(value) ||
        (object != null && value in Object(object))
    )
}

/**
 * Converts `string` to a property path array.
 * @private
 */
function stringToPath(string: string): string[] {
    const result: string[] = []
    if (string.charCodeAt(0) === 46 /* . */) {
        result.push('')
    }
    string.replace(
        rePropName,
        (match: string, number: string, quote: string, subString: string): string => {
            result.push(quote ? subString.replace(reEscapeChar, '$1') : number || match)
            return ''
        }
    )
    return result
}

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 * @private
 */
function toString(value: any): string {
    return value == null ? '' : value + ''
}

/**
 * Converts `value` to a string key if it's not a string or symbol.
 * @private
 */
function toKey(value: any): string {
    if (typeof value === 'string' || isSymbol(value)) {
        return value
    }
    const result = value + ''
    return result === '0' && 1 / value === -INFINITY ? '-0' : result
}

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 * @private
 */
function isSymbol(value: any): boolean {
    return (
        typeof value === 'symbol' ||
        (isObjectLike(value) && objectToString.call(value) === '[object Symbol]')
    )
}

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 * @private
 */
function isObjectLike(value: any): boolean {
    return value != null && typeof value === 'object'
}

// Constants and regex patterns
const INFINITY = 1 / 0
const MAX_SAFE_INTEGER = 9007199254740991
const objectToString = Object.prototype.toString
const hasOwnProperty = Object.prototype.hasOwnProperty

/** Used to match property names within property paths. */
const rePropName =
    /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*)?\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g

/** Used to match backslashes in property paths. */
const reEscapeChar = /\\(\\)?/g

/** Used to detect unsigned integer values. */
const reIsUint = /^(?:0|[1-9]\d*)$/

/** Used to match property names within property paths. */
const reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/
const reIsPlainProp = /^\w*$/

/** Detect free variable `global` from Node.js. */
const freeGlobal = typeof global === 'object' && global && global.Object === Object && global

/** Detect free variable `self`. */
const freeSelf = typeof self === 'object' && self && self.Object === Object && self

/** Used as a reference to the global object. */
const root = freeGlobal || freeSelf || Function('return this')()

/** Built-in method references without a dependency on `root`. */
const defineProperty = (function () {
    try {
        const func = getNative(Object, 'defineProperty')
        func({}, '', {})
        return func
    } catch (e) {
        return undefined
    }
})()

/**
 * Gets the native function at `key` of `object`.
 * @private
 */
function getNative(object: any, key: string): any {
    const value = getValue(object, key)
    return isNative(value) ? value : undefined
}

/**
 * Gets the value at `key` of `object`.
 * @private
 */
function getValue(object: any, key: string): any {
    return object == null ? undefined : object[key]
}

/**
 * Checks if `value` is a native function.
 * @private
 */
function isNative(value: any): boolean {
    if (!isObject(value)) {
        return false
    }
    const pattern = isFunction(value) ? reIsNative : reIsHostCtor
    return pattern.test(toSource(value))
}

/**
 * Checks if `value` is classified as a `Function` object.
 * @private
 */
function isFunction(value: any): boolean {
    if (!isObject(value)) {
        return false
    }
    // The use of `Object#toString` avoids issues with the `typeof` operator
    // in Safari 9 which returns 'object' for typed arrays and other constructors.
    const tag = objectToString.call(value)
    return (
        tag === '[object Function]' ||
        tag === '[object GeneratorFunction]' ||
        tag === '[object AsyncFunction]' ||
        tag === '[object Proxy]'
    )
}

/**
 * Converts `func` to its source code.
 * @private
 */
function toSource(func: Function): string {
    if (func != null) {
        try {
            return funcToString.call(func)
        } catch (e) {}
        try {
            return func + ''
        } catch (e) {}
    }
    return ''
}

/** Used to resolve the decompiled source of functions. */
const funcToString = Function.prototype.toString

/** Used to detect if a method is native. */
const reIsNative = /^\[object .+?Constructor\]$/

/** Used to detect host constructors (Safari). */
const reIsHostCtor = /^\[object .+?Constructor\]$/

export default SetTask
